<template>
  <div class="space-y-4">
    <div v-if="title" class="flex items-center justify-between">
      <h4 class="text-sm font-medium text-gray-700">{{ title }}</h4>
      <button
        type="button"
        @click="autoTranslate"
        :disabled="isTranslating"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <svg v-if="isTranslating" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span v-else>🤖</span>
        {{ isTranslating ? 'Translating...' : 'AI Traduction' }}
      </button>
    </div>
    
    <div class="space-y-3">
      <!-- Français -->
      <div class="flex items-start gap-3">
        <img src="@/assets/images/france.png" alt="Français" class="w-6 h-4 mt-2" />
        <textarea 
          :value="modelValue.francais"
          @input="updateValue('francais', ($event.target as HTMLTextAreaElement).value)"
          :rows="rows"
          :placeholder="placeholders.francais || 'Description en français...'"
          :required="required"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        ></textarea>
      </div>
      
      <!-- Anglais -->
      <div class="flex items-start gap-3">
        <img src="@/assets/images/royaume-uni.png" alt="Anglais" class="w-6 h-4 mt-2" />
        <textarea 
          :value="modelValue.anglais"
          @input="updateValue('anglais', ($event.target as HTMLTextAreaElement).value)"
          :rows="rows"
          :placeholder="placeholders.anglais || 'Description en anglais...'"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        ></textarea>
      </div>
      
      <!-- Espagnol -->
      <div class="flex items-start gap-3">
        <img src="@/assets/images/espagne.png" alt="Espagnol" class="w-6 h-4 mt-2" />
        <textarea 
          :value="modelValue.espagnol"
          @input="updateValue('espagnol', ($event.target as HTMLTextAreaElement).value)"
          :rows="rows"
          :placeholder="placeholders.espagnol || 'Description en espagnol...'"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        ></textarea>
      </div>
      
      <!-- Italien -->
      <div class="flex items-start gap-3">
        <img src="@/assets/images/italie.png" alt="Italien" class="w-6 h-4 mt-2" />
        <textarea 
          :value="modelValue.italien"
          @input="updateValue('italien', ($event.target as HTMLTextAreaElement).value)"
          :rows="rows"
          :placeholder="placeholders.italien || 'Description en italien...'"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { fetchRest, getApiBaseUrl } from '@/config/api'

interface MultilingualText {
  francais: string
  anglais: string
  espagnol: string
  italien: string
}

interface Props {
  modelValue: MultilingualText
  title?: string
  rows?: number
  required?: boolean
  placeholders?: Partial<MultilingualText>
}

interface Emits {
  (e: 'update:modelValue', value: MultilingualText): void
}

const props = withDefaults(defineProps<Props>(), {
  rows: 3,
  required: false,
  placeholders: () => ({})
})

const emit = defineEmits<Emits>()
const isTranslating = ref(false)

const updateValue = (lang: keyof MultilingualText, value: string) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [lang]: value
  })
}

const ALL_LANGS: (keyof MultilingualText)[] = ['francais', 'anglais', 'espagnol', 'italien']

async function autoTranslate() {
  // 找到第一个有内容的语言作为源语言
  const sourceLang = ALL_LANGS.find(lang => props.modelValue[lang]?.trim())

  if (!sourceLang) {
    alert('Veuillez remplir au moins une description dans une langue.')
    return
  }

  const text = props.modelValue[sourceLang].trim()
  const targetLangs = ALL_LANGS.filter(lang => lang !== sourceLang)

  isTranslating.value = true
  try {
    const API_URL = getApiBaseUrl()
    const response = await fetchRest(`${API_URL}/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, sourceLang, targetLangs }),
    })

    if (!response.ok) {
      throw new Error('Translation request failed')
    }

    const data = await response.json()

    // 更新所有翻译结果
    const updated = { ...props.modelValue }
    for (const lang of targetLangs) {
      if (data[lang]) {
        updated[lang as keyof MultilingualText] = data[lang]
      }
    }
    emit('update:modelValue', updated)
  } catch (err) {
    console.error('Translation error:', err)
    alert('Erreur lors de la traduction. Veuillez réessayer.')
  } finally {
    isTranslating.value = false
  }
}

// Indique si une traduction de secours est nécessaire :
// au moins une langue remplie mais au moins une autre vide.
function needsAutoTranslate(): boolean {
  const filled = ALL_LANGS.filter(lang => props.modelValue[lang]?.trim())
  return filled.length > 0 && filled.length < ALL_LANGS.length
}

// Exposé au parent pour permettre la traduction automatique à l'enregistrement
// lorsque l'utilisateur n'a pas cliqué sur "AI Traduction".
defineExpose({ autoTranslate, needsAutoTranslate })
</script>

